import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { InfoCircleOutlined } from "@ant-design/icons";
import { App, Button, Form, theme } from "antd";
import { observer } from "mobx-react";
import { useCallback, useState } from "react";
import { AffiliateGeneralFields } from "./affiliateGeneralFields.js";
import { AffiliateInvoiceAddressFields } from "./affiliateInvoiceAddressFields.js";
import { AffiliatePaymentDetailsFieldSet } from "./affiliatePaymentDetailsFieldSet.js";
import { AFFILIATE_INVOICE_ADDRESS_CHANGE_FIELDS } from "./changeFields.js";
import { RealCommerceError } from "../../../../../../exceptions/realCommerceError.js";
import { useRealCommerceErrorHandling } from "../../../../../../hooks/useRealCommerceErrorHandling.js";
import { trackAnalyticsEvent } from "../../../../../../others/tracking.js";
import { useStores } from "../../../../../../store/stores.js";
import { __, _i } from "../../../../../../utils/i18n.js";
const AffiliateInvoiceDataForm = observer(()=>{
    const { token } = theme.useToken();
    const { message } = App.useApp();
    const [isBusy, setIsBusy] = useState(false);
    const [tradeRegisterExcerptFile, setTradeRegisterExcerptFile] = useState();
    const [form] = Form.useForm();
    const handleRealCommerceError = useRealCommerceErrorHandling();
    const { accountV2Store: { session } } = useStores();
    const affiliate = session?.affiliate;
    const onFinish = useCallback(async ({ affiliate: formAffiliate })=>{
        if (!affiliate) {
            return;
        }
        const { invoiceAddress } = formAffiliate;
        // Switzerland and similar: UID doubles as the tax number.
        if (!invoiceAddress.taxNumber) {
            invoiceAddress.taxNumber = invoiceAddress.companyIdentificationNumber;
        }
        setIsBusy(true);
        try {
            const invoiceAddressGotChanged = !!tradeRegisterExcerptFile || JSON.stringify(AFFILIATE_INVOICE_ADDRESS_CHANGE_FIELDS.map((k)=>invoiceAddress[k])) !== JSON.stringify(AFFILIATE_INVOICE_ADDRESS_CHANGE_FIELDS.map((k)=>affiliate.affiliate.invoiceAddress[k]));
            await affiliate.updateDetails({
                ...formAffiliate,
                invoiceAddress: invoiceAddressGotChanged ? {
                    ...invoiceAddress,
                    tradeRegisterExcerpt: tradeRegisterExcerptFile
                } : undefined,
                // Truncated payment details are only sent when actually re-entered.
                paymentDetails: formAffiliate.paymentDetails.isTruncate ? undefined : formAffiliate.paymentDetails
            });
            trackAnalyticsEvent("affiliate_invoice_details_saved", {
                ga: {
                    event_category: "account",
                    event_label: "Affiliate invoice details saved"
                },
                matomo: {
                    category: "account",
                    name: "Affiliate invoice details saved"
                }
            });
            message.success(__("Your invoice and payment data are saved."));
        } catch (e) {
            if (e instanceof RealCommerceError) {
                handleRealCommerceError(e);
            }
        } finally{
            setIsBusy(false);
        }
    }, [
        affiliate,
        tradeRegisterExcerptFile
    ]);
    if (!affiliate) {
        return null;
    }
    const { communicationLanguage, payoutThreshold, invoiceAddress, paymentDetails } = affiliate.affiliate;
    const initialValues = {
        affiliate: {
            communicationLanguage,
            payoutThreshold,
            invoiceAddress: {
                companyName: invoiceAddress.companyName,
                country: invoiceAddress.country,
                firstName: invoiceAddress.firstName,
                lastName: invoiceAddress.lastName,
                street: invoiceAddress.street,
                zip: invoiceAddress.zip,
                city: invoiceAddress.city,
                companyIdentificationNumber: invoiceAddress.companyIdentificationNumber,
                taxNumber: invoiceAddress.taxNumber,
                isSmallBusinessWithoutVatDeclaration: invoiceAddress.isSmallBusinessWithoutVatDeclaration,
                vatId: invoiceAddress.vatId
            },
            paymentDetails: {
                accountHolder: paymentDetails.accountHolder,
                currency: paymentDetails.currency,
                achAccountNumber: paymentDetails.achAccountNumber,
                achRoutingNumber: paymentDetails.achRoutingNumber,
                iban: paymentDetails.iban,
                ukAccountNumber: paymentDetails.ukAccountNumber,
                ukSortCode: paymentDetails.ukSortCode,
                isTruncate: true
            }
        },
        user: {
            email: affiliate.affiliate.user.email
        }
    };
    return /*#__PURE__*/ _jsxs(Form, {
        form: form,
        onFinish: onFinish,
        initialValues: initialValues,
        layout: "vertical",
        colon: false,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-affiliate-note",
                style: {
                    marginBottom: 16
                },
                children: [
                    /*#__PURE__*/ _jsx(InfoCircleOutlined, {
                        style: {
                            color: token.colorPrimary
                        }
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: _i(__("Changes here do not change the contract data and payment data of your subscriptions. Those are managed on the {{a}}Subscriptions and Licenses{{/a}} tab."), {
                            a: /*#__PURE__*/ _jsx("a", {
                                href: "#/subscriptions"
                            })
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(AffiliateGeneralFields, {
                isEdit: true
            }),
            /*#__PURE__*/ _jsx(AffiliateInvoiceAddressFields, {
                isEdit: true,
                setTradeRegisterExcerptFile: setTradeRegisterExcerptFile
            }),
            /*#__PURE__*/ _jsx(AffiliatePaymentDetailsFieldSet, {
                form: form,
                isEdit: true
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                style: {
                    textAlign: "right",
                    marginBottom: 0
                },
                children: /*#__PURE__*/ _jsx(Button, {
                    type: "primary",
                    htmlType: "submit",
                    loading: isBusy,
                    children: __("Save changes")
                })
            })
        ]
    });
});
export { AffiliateInvoiceDataForm };
